import * as React from 'react';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
// mobx
import { inject } from 'mobx-react';

import { PzStore, UiStore, UserAuthStore } from 'stores';
import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { PositionType, UitkMenu, UitkMenuContainer, UitkMenuList, UitkMenuTrigger } from 'uitk-react-menu';
import { UitkText } from 'uitk-react-text';
import { analyticsEvent, getUserAnalyticsMicroMessage } from '../../util/analytics';
import { useLocation } from 'react-router-dom';

export interface LanguageSelectorProps {
  uiStore?: UiStore;
  context?: ContextStore;
  analytics?: AnalyticsStore;
  userAuth?: UserAuthStore;
  pzStore?: PzStore;
}

export const LanguageSelector: React.FunctionComponent<LanguageSelectorProps> = inject(
  'uiStore',
  'context',
  'analytics',
  'userAuth',
  'pzStore',
)(({ uiStore, context, analytics, userAuth, pzStore }) => {
  const location = useLocation();

  const { supportedLocales } = uiStore;
  const currentLang =
    supportedLocales &&
    supportedLocales.find((locale) => {
      return locale.langId === context.langId;
    });

  React.useEffect(() => {
    const storedLocale = localStorage.getItem('locale');
    if (!storedLocale || context.locale !== storedLocale) {
      localStorage.setItem('locale', context.locale);
    }
  }, []);

  // Set state
  const [activeLabel, setActiveLabel] = React.useState(currentLang.langLabel);
  const [open, setOpen] = React.useState(false);

  let pathWithoutLocale: string;
  let path: string;

  // Do this for every page other than the first homepage load
  if (location.pathname !== '/home') {
    // Get path from the route, strip out locale
    const pathArray = location.pathname.split('/');
    const param = location.search;

    if (pathArray) {
      // take out first pieces of URL '' and '/xx-xx/'
      pathArray.splice(0, 2);
      pathWithoutLocale = pathArray && pathArray.join('/');
    }

    path = param ? pathWithoutLocale + param : pathWithoutLocale;
    // Homepage starts without locale prefix so don't try remove
  } else {
    path = 'home';
  }

  const langList =
    supportedLocales &&
    supportedLocales.map((lang, i) => {
      return {
        attributes: {
          role: 'menuitem',
          tabindex: i + 1,
        },
        label: lang.langLabel,
        className: 'uitk-list-item ' + (context.langId === lang.langId ? 'langSelected' : ''),
        href: `/${lang.langCode}/${path}`,
        typeAnchor: true,
        onClick: (e) => handleItemClick(e, lang.langLabel),
        analytics: {
          id: 'affhub_language_switcher.submitted',
          callback() {
            analyticsTrackEvents('affhub_language_switcher.submitted');
          },
        },
      };
    });

  function handleItemClick(e, label) {
    setActiveLabel(label);
    setOpen(false);
  }

  function handleTriggerClicked(e) {
    setOpen(!open);
  }

  const analyticsTrackEvents = (rfrr: string) => {
    const linkname = {
      'affhub_language_switcher.selected': 'Affhub Language Selected',
      'affhub_language_switcher.submitted': 'Affhub Language Switched',
    }[rfrr];

    analyticsEvent(analytics, userAuth, pzStore, rfrr, linkname);
  };

  const trackMenuOpen = {
    id: 'affhub_language_switcher.selected',
    callback() {
      analyticsTrackEvents('affhub_language_switcher.selected');
    },
  };

  return (
    <UitkMenu isOpen={open} onTriggerClick={(e) => handleTriggerClicked(e)}>
      <UitkMenuTrigger>
        <UitkTertiaryButton analytics={trackMenuOpen}>
          <UitkIcon name="language" className="Language__icon" />
          <UitkText size={300} className="visible-desktop--large">
            {activeLabel}
          </UitkText>
        </UitkTertiaryButton>
      </UitkMenuTrigger>
      <UitkMenuContainer position={PositionType.RIGHT}>
        <UitkMenuList items={langList} />
      </UitkMenuContainer>
    </UitkMenu>
  );
});
